import $script from 'scriptjs';
import axios from 'axios';
import { triggerCustomEvent } from 'lib/helpers';
import Cookies from 'js-cookie';
import {
  findReferringCategory,
  trackCompleteEvent,
} from 'components/analytics/conversions/tracking';
import { JOIN_COMPLETE, LOGIN_COMPLETE } from 'components/analytics/conversions/constants/actions';
import {
  fromSignup,
  removeSocialOauthCookie,
  showSignupSurvey,
  socialSessionLoading,
  socialSessionError,
  socialSessionFromFollow,
} from 'lib/social-session-helpers';

export default class FacebookLogin {
  constructor({
    buttonTarget, textTarget = null, csrfToken, omniauthToken,
  }) {
    this.buttonTarget = buttonTarget;
    this.textTarget = textTarget;
    this.csrfToken = csrfToken;
    this.omniauthToken = omniauthToken;

    this.connectType = buttonTarget.getAttribute('data-oauth-type');
    this.handleFacebookResponse = this.handleFacebookResponse.bind(this);
    this.handleError = this.handleError.bind(this);

    this.init();
  }

  init() {
    socialSessionError(false, this.buttonTarget);

    this.authType = 'reauthorize';

    $script('https://connect.facebook.net/en_US/sdk.js', this.loadFacebookApi);
  }

  loadFacebookApi = () => {
    window.FB.init({
      appId: this.buttonTarget.dataset.appId,
      autoLogAppEvents: true,
      cookie: true,
      status: true,
      xfbml: false,
      version: 'v3.2',
    });
    this.buttonTarget.dataset.loaded = 'true';
  }

  connect() {
    const newsletter = document.getElementById('user_newsletter');
    let newsletterValue = false;
    if (newsletter !== null && newsletter.type !== 'hidden') {
      newsletterValue = newsletter.checked;
    } else if (newsletter !== null && newsletter.type === 'hidden') {
      newsletterValue = true;
    }

    const { omniauthPath } = this.buttonTarget.dataset;
    const url = `${omniauthPath}/callback?newsletter=${newsletterValue}&state=${this.omniauthToken}`;
    this.buttonTarget.dataset.omniauthPath = url;

    if (this.textTarget) {
      this.originalText = this.textTarget.innerHTML;

      socialSessionLoading(this.buttonTarget);
    }

    this.login().then(
      () => {
        this.validate().then(
          () => {
            this.handleFacebookResponse();
          },
          () => {
            this.loginReject(true);
          },
        );
      },
      () => {
        this.loginReject();
      },
    );
  }

  handleFacebookResponse = () => {
    axios.defaults.headers.common['X-CSRF-Token'] = this.csrfToken;

    axios.post(this.buttonTarget.dataset.omniauthPath, this.formDataForAjaxPost, { withCredentials: true })
      .then((response) => {
        this.responseSuccess(response);
      })
      .catch(this.handleError);
  }

  get formDataForAjaxPost() {
    const bodyFormData = new FormData();
    bodyFormData.append('authenticity_token', this.csrfToken);

    return bodyFormData;
  }

  responseSuccess = (response) => {
    if (response.status === 200 || response.status === 204) {
      this.processResponse(response);
    } else { this.handleError(response.error); }

    removeSocialOauthCookie();
  }

  processResponse(response) {
    // using the social_oauth_type cookie to check if it's javascript
    if (fromSignup()) {
      // we have to eval the modal injector code coming back
      if (showSignupSurvey(response)) eval(response.data); // eslint-disable-line no-eval

      trackCompleteEvent({
        action: JOIN_COMPLETE,
        category: findReferringCategory(this.buttonTarget),
        label: 'Sign_Up-Facebook',
        dataLayerCompleteAttrs: [
          'email_address',
          'email_hash_md5',
          'email_hash_sha1',
          'sign_in_type',
        ],
      });

      return;
    }

    trackCompleteEvent({
      action: LOGIN_COMPLETE,
      category: findReferringCategory(this.buttonTarget),
      label: 'Sign_In-Facebook',
      dataLayerCompleteAttrs: [
        'email_address',
        'email_hash_md5',
        'email_hash_sha1',
        'sign_in_type',
        'name',
        'country',
        'postal_code',
        'team_owner_email',
        'team_seats',
        'multiple_logins',
      ],
    });

    if (this.fromFollow) {
      window.location.reload();
    } else if (response.data.message === 'login_in_place') {
      triggerCustomEvent('LoggedInPlace');
    } else if (Cookies.get('sign_in_referrer')) {
      window.location.assign(Cookies.get('sign_in_referrer'));
    } else {
      window.location.reload();
    }
  }

  get fromFollow() {
    return socialSessionFromFollow();
  }

  loginReject = (error = false) => {
    if (this.textTarget) socialSessionLoading(this.buttonTarget, this.originalText);
    socialSessionError(error, this.buttonTarget);

    if (error) this.authType = 'rerequest';
  }

  login() {
    return new Promise((resolve, reject) => {
      window.FB.login(
        (response) => {
          if (response.status === 'connected') {
            resolve();
          } else {
            reject();
          }
        },
        { scope: 'public_profile,email', auth_type: this.authType },
      );
    });
  }

  validate() {
    return new Promise((resolve, reject) => {
      window.FB.api('/me/permissions', (response) => {
        response.data.forEach((permission) => {
          if (
            permission.permission === 'email'
            && permission.status !== 'granted'
          ) {
            reject();
          }

          if (
            permission.permission === 'public_profile'
            && permission.status !== 'granted'
          ) {
            reject();
          }
        });

        resolve();
      });
    });
  }

  handleError = (error) => {
    if (error) console.error(error); // eslint-disable-line no-console
    if (this.textTarget) socialSessionLoading(this.buttonTarget, this.originalText);
    socialSessionError(true, this.buttonTarget);
  }
}
